import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './SiteManagement.css';

function SiteManagement() {
  const { user, apiFetch } = useAuth();
  const { t } = useI18n();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    siteId: '',
    name: '',
    apiBaseUrl: '',
    apiToken: '',
    dbBridgeToken: '',
    databaseName: '',
    creationCode: '',
    dbHost: '',
    dbPort: '',
    dbUser: '',
    allowInsecureTls: false,
    isActive: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const [serverCfgSnippet, setServerCfgSnippet] = useState('');
  const [creationCodes, setCreationCodes] = useState([]);
  const [newCreationCode, setNewCreationCode] = useState('');
  const [newCreationCodePlan, setNewCreationCodePlan] = useState('basic');
  const [newCreationCodeExpiresAt, setNewCreationCodeExpiresAt] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const buildServerCfgSnippet = (site) => {
    if (!site) return '';
    const lines = [
      `# Void-link configuration for site ${site.site_id} (${site.name})`,
      `set void_link_secret_token "${site.api_token || ''}"`,
      `set void_link_db_token "${site.db_bridge_token || ''}"`,
      `set void_link_enable_db_bridge "1"`,
      `set void_link_db_max_query_length "5000"`,
      `set void_link_db_max_params "100"`,
      `set void_link_db_rate_limit_window_ms "60000"`,
      `set void_link_db_rate_limit_max "180"`,
      `# Optional database settings for local IFR without .env`,
      site.db_host ? `set void_link_db_host "${site.db_host}"` : '# set void_link_db_host "127.0.0.1"',
      site.db_port ? `set void_link_db_port "${site.db_port}"` : '# set void_link_db_port "3306"',
      site.db_user ? `set void_link_db_user "${site.db_user}"` : '# set void_link_db_user "your_db_user"',
      `set void_link_api_base_url "${site.api_base_url || ''}"`,
      `set void_link_database_name "${site.database_name || ''}"`,
      `# This file does not store sensitive panel token values. Use site API settings on UI.`
    ];
    return lines.join('\n');
  };

  const fetchSites = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch('/api/sites');
      const text = await response.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
      if (!response.ok) {
        const msg = (data && (data.error || data.message)) ? (data.error || data.message) : (text || `HTTP ${response.status}`);
        throw new Error(msg);
      }
      setSites((data && data.sites) ? data.sites : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreationCodes = async () => {
    try {
      const response = await apiFetch('/api/site-creation-codes');
      const text = await response.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
      if (!response.ok) {
        const msg = (data && (data.error || data.message)) ? (data.error || data.message) : (text || `HTTP ${response.status}`);
        throw new Error(msg);
      }
      setCreationCodes((data && data.codes) ? data.codes : []);
    } catch (err) {
      console.warn('Could not load creation codes:', err.message);
    }
  };

  useEffect(() => {
    // Intentionally only run once on mount; fetchSites/fetchCreationCodes are stable here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSites();
    fetchCreationCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({
      siteId: '',
      name: '',
      apiBaseUrl: '',
      apiToken: '',
      dbBridgeToken: '',
      databaseName: '',
      creationCode: '',
      dbHost: '',
      dbPort: '',
      dbUser: '',
      allowInsecureTls: false,
      isActive: true
    });
    setIsEditing(false);
    setShowCreateForm(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const url = isEditing ? `/api/sites/${encodeURIComponent(form.siteId)}` : '/api/sites';
      const method = isEditing ? 'PUT' : 'POST';

      const payload = {
        siteId: (form.siteId || '').trim(),
        name: (form.name || '').trim(),
        subscriptionPlan: form.subscriptionPlan || 'basic',
        billingStatus: form.billingStatus || 'active',
        isActive: !!form.isActive
      };

      if (form.creationCode) payload.creationCode = form.creationCode.trim();

      // Include advanced configuration only when not creating as an owner
      if (!(user?.role === 'owner' && !isEditing)) {
        if (form.apiBaseUrl) payload.apiBaseUrl = form.apiBaseUrl.trim();
        if (form.apiToken) payload.apiToken = form.apiToken.trim();
        if (form.dbBridgeToken) payload.dbBridgeToken = form.dbBridgeToken.trim();
        if (form.databaseName) payload.databaseName = form.databaseName.trim();
        if (form.dbHost) payload.dbHost = form.dbHost.trim();
        if (form.dbPort) payload.dbPort = Number(form.dbPort) || null;
        if (form.dbUser) payload.dbUser = form.dbUser.trim();
        payload.allowInsecureTls = !!form.allowInsecureTls;
      }

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const text = await response.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
      if (!response.ok) {
        const msg = (data && (data.error || data.message)) ? (data.error || data.message) : (text || `HTTP ${response.status}`);
        throw new Error(msg);
      }

      await fetchSites();
      setServerCfgSnippet(buildServerCfgSnippet((data && data.site) ? data.site : payload));
      resetForm();
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
  };

  const handleEdit = (site) => {
    setForm({
      siteId: site.site_id,
      name: site.name,
      apiBaseUrl: site.api_base_url,
      apiToken: site.api_token || '',
      dbBridgeToken: site.db_bridge_token || '',
      databaseName: site.database_name,
      creationCode: '',
      subscriptionPlan: site.subscription_plan || 'basic',
      billingStatus: site.billing_status || 'active',
      dbHost: site.db_host || '',
      dbPort: site.db_port || '',
      dbUser: site.db_user || '',
      allowInsecureTls: !!site.allow_insecure_tls,
      isActive: !!site.is_active
    });
    setServerCfgSnippet(buildServerCfgSnippet(site));
    setIsEditing(true);
    setShowCreateForm(true);
    setError('');
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm(`Delete site ${siteId}?`)) return;
    try {
      const response = await apiFetch(`/api/sites/${encodeURIComponent(siteId)}`, { method: 'DELETE' });
      const text = await response.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
      if (!response.ok) {
        const msg = (data && (data.error || data.message)) ? (data.error || data.message) : (text || `HTTP ${response.status}`);
        throw new Error(msg);
      }
      await fetchSites();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="site-management">

      {error && <div className="sm-error">{error}</div>}

      {!showCreateForm && !isEditing && (
    <button className="sm-btn-primary" onClick={() => setShowCreateForm(true)} style={{ marginBottom: 16 }}>
      {t('siteManagement.addSite')}
    </button>
  )}

  {(showCreateForm || isEditing) && (
        <form className="sm-form" onSubmit={handleSubmit}>
          <h3>{isEditing ? t('siteManagement.editSiteTitle') : t('siteManagement.createSiteTitle')}</h3>
          <div className="sm-row">
            <label>{t('siteManagement.siteId')}</label>
            <input
              value={form.siteId}
              onChange={(e) => handleChange('siteId', e.target.value)}
              required
              disabled={isEditing}
            />
          </div>
          <div className="sm-row">
            <label>{t('siteManagement.name')}</label>
            <input
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </div>
          {!(!isEditing && user?.role === 'owner') && (
            <>
              <div className="sm-row">
                <label>{t('siteManagement.apiBaseUrl')}</label>
                <input
                  value={form.apiBaseUrl}
                  onChange={(e) => handleChange('apiBaseUrl', e.target.value)}
                  required
                />
              </div>
              <div className="sm-row">
                <label>{t('siteManagement.apiToken')}</label>
                <input
                  value={form.apiToken}
                  onChange={(e) => handleChange('apiToken', e.target.value)}
                  required={!isEditing}
                />
              </div>
              <div className="sm-row">
                <label>{t('siteManagement.dbBridgeToken')}</label>
                <input
                  value={form.dbBridgeToken}
                  onChange={(e) => handleChange('dbBridgeToken', e.target.value)}
                  required={!isEditing}
                />
              </div>
              <div className="sm-row">
                <label>{t('siteManagement.databaseName')}</label>
                <input
                  value={form.databaseName}
                  onChange={(e) => handleChange('databaseName', e.target.value)}
                  required
                />
              </div>
            </>
          )}
          {!isEditing && (
            <div className="sm-row">
              <label>{t('siteManagement.creationCode')}</label>
              <input
                value={form.creationCode}
                onChange={(e) => handleChange('creationCode', e.target.value)}
                placeholder={t('siteManagement.creationCodePlaceholder')}
                required
              />
            </div>
          )}
          <div className="sm-row">
            <label>{t('siteManagement.subscriptionPlan')}</label>
            <select
              value={form.subscriptionPlan}
              onChange={(e) => handleChange('subscriptionPlan', e.target.value)}
            >
              <option value="basic">Basic</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
            </select>
          </div>
          <div className="sm-row">
            <label>{t('siteManagement.billingStatus')}</label>
            <select
              value={form.billingStatus}
              onChange={(e) => handleChange('billingStatus', e.target.value)}
            >
              <option value="active">Active</option>
              <option value="past_due">Past Due</option>
              <option value="cancelled">Cancelled</option>
              <option value="trial">Trial</option>
            </select>
          </div>
          {!(!isEditing && user?.role === 'owner') && (
            <>
              <div className="sm-row">
                <label>{t('siteManagement.dbHost') || 'Game DB Host'}</label>
                <input
                  value={form.dbHost}
                  onChange={(e) => handleChange('dbHost', e.target.value)}
                />
              </div>
              <div className="sm-row">
                <label>{t('siteManagement.dbPort') || 'Game DB Port'}</label>
                <input
                  value={form.dbPort}
                  onChange={(e) => handleChange('dbPort', e.target.value)}
                  type="number"
                />
              </div>
              <div className="sm-row">
                <label>{t('siteManagement.dbUser') || 'Game DB User'}</label>
                <input
                  value={form.dbUser}
                  onChange={(e) => handleChange('dbUser', e.target.value)}
                />
              </div>
              <div className="sm-row checkbox-row">
                <label>
                  <input
                    type="checkbox"
                    checked={form.allowInsecureTls}
                    onChange={(e) => handleChange('allowInsecureTls', e.target.checked)}
                  />
                  Allow insecure TLS
                </label>
              </div>
            </>
          )}
          <div className="sm-row checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
              />
              Active
            </label>
          </div>
          <div className="sm-actions">
            <button type="submit" className="sm-btn-primary">{isEditing ? t('siteManagement.update') : t('siteManagement.create')}</button>
            {isEditing && <button type="button" className="sm-btn-secondary" onClick={resetForm}>{t('siteManagement.cancel')}</button>}
            {!isEditing && <button type="button" className="sm-btn-secondary" onClick={resetForm}>{t('siteManagement.cancel')}</button>}
          </div>
        </form>
      )}

      <section>
        <h3>{t('siteManagement.existingSites')}</h3>
        {loading ? <p>{t('app.loading')}</p> : (
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t('siteManagement.table.siteId')}</th>
                <th>{t('siteManagement.table.name')}</th>
                <th>{t('siteManagement.table.apiBaseUrl')}</th>
                <th>{t('siteManagement.table.dbName')}</th>
                <th>{t('siteManagement.table.plan')}</th>
                <th>{t('siteManagement.table.billing')}</th>
                <th>{t('siteManagement.table.active')}</th>
                <th>{t('siteManagement.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.site_id}>
                  <td>{site.site_id}</td>
                  <td>{site.name}</td>
                  <td>{site.api_base_url}</td>
                  <td>{site.database_name}</td>
                  <td>{site.subscription_plan || 'basic'}</td>
                  <td>{site.billing_status || 'active'}</td>
                  <td>{site.is_active ? t('siteManagement.yes') : t('siteManagement.no')}</td>
                  <td>
                    <button type="button" onClick={() => handleEdit(site)}>Edit</button>
                    <button type="button" onClick={() => handleDelete(site.site_id)}>Delete</button>
                      <button type="button" onClick={async () => {
                      const newStatus = site.billing_status === 'active' ? 'cancelled' : 'active';
                      try {
                        const response = await apiFetch(`/api/sites/${encodeURIComponent(site.site_id)}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ billingStatus: newStatus })
                        });
                        const text = await response.text();
                        let data = null;
                        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
                        if (!response.ok) {
                          const msg = (data && (data.error || data.message)) ? (data.error || data.message) : (text || `HTTP ${response.status}`);
                          throw new Error(msg);
                        }
                        await fetchSites();
                      } catch (err) {
                        setError(err.message);
                      }
                    }}>
                      {site.billing_status === 'active' ? t('siteManagement.deactivate') : t('siteManagement.activate')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3>{t('siteManagement.creationCodesTitle')}</h3>
        <form className="sm-form" onSubmit={async (e) => {
          e.preventDefault();
          if (!newCreationCode) return;
          try {
            const response = await apiFetch('/api/site-creation-codes', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                code: newCreationCode,
                description: `Plan: ${newCreationCodePlan}`,
                plan: newCreationCodePlan,
                expiresAt: newCreationCodeExpiresAt || null,
                isActive: true
              })
            });
            const text = await response.text();
            let data = null;
            try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
            if (!response.ok) {
              const msg = (data && (data.error || data.message)) ? (data.error || data.message) : (text || `HTTP ${response.status}`);
              throw new Error(msg);
            }
            setNewCreationCode('');
            setNewCreationCodePlan('basic');
            setNewCreationCodeExpiresAt('');
            await fetchCreationCodes();
          } catch (err) {
            setError(err.message);
          }
        }}>
          <div className="sm-row">
            <label>{t('siteManagement.codeColumn')}</label>
            <input
              value={newCreationCode}
              onChange={(e) => setNewCreationCode(e.target.value)}
              placeholder={t('siteManagement.newCreationCodePlaceholder')}
              required
            />
          </div>
          <div className="sm-row">
            <label>{t('siteManagement.planLabel')}</label>
            <select value={newCreationCodePlan} onChange={(e) => setNewCreationCodePlan(e.target.value)}>
              <option value="basic">Basic</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
            </select>
          </div>
          <div className="sm-row">
            <label>{t('siteManagement.expiresAt')}</label>
            <input
              type="datetime-local"
              value={newCreationCodeExpiresAt}
              onChange={(e) => setNewCreationCodeExpiresAt(e.target.value)}
            />
          </div>
          <div className="sm-actions">
            <button type="submit" className="sm-btn-primary">{t('siteManagement.createCodeButton')}</button>
          </div>
        </form>

        <table className="sm-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Plan</th>
              <th>Expires</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {creationCodes.map((c) => (
              <tr key={c.id}>
                <td>{c.code}</td>
                <td>{c.plan || 'basic'}</td>
                <td>{c.expires_at || 'none'}</td>
                  <td>{c.is_active ? t('siteManagement.yes') : t('siteManagement.no')}</td>
                <td>
                    <button type="button" onClick={async () => {
                      try {
                        const response = await apiFetch(`/api/site-creation-codes/${c.id}`, { method: 'DELETE' });
                        const text = await response.text();
                        let data = null;
                        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
                        if (!response.ok) {
                          const msg = (data && (data.error || data.message)) ? (data.error || data.message) : (text || `HTTP ${response.status}`);
                          throw new Error(msg);
                        }
                        await fetchCreationCodes();
                      } catch (err) {
                        setError(err.message);
                      }
                    }}>
                    {t('siteManagement.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {serverCfgSnippet && (
        <section>
          <h3>Generated server.cfg snippet</h3>
          <p>Copy these lines into the game server <code>server.cfg</code> to configure void-link automatically.</p>
          <pre className="sm-snippet">{serverCfgSnippet}</pre>
        </section>
      )}
    </div>
  );
}

export default SiteManagement;
